import React, { useState } from "react"
import Dialog from "@reach/dialog"
import { useQuery } from "react-query"
import { request } from "../../util/request"
import { RegistrationForm } from "./RegistrationForm"
import { AuthenticationForm } from "./AuthenticationForm"
import { ResetPasswordForm } from "./ResetPasswordForm"
import "./AuthenticationDialog.css"

export type ModalState = "auth" | "register" | "reset_password"

export interface AuthFlowData {
  flow: string
  current_location?: string
  referral_token?: string
  follow_band_id?: string | number | null
  user: { password?: string; email?: string; [key: string]: unknown }
}

interface BandResponse {
  band?: { cover_photo_url_large?: string; name?: string }
}

export interface AuthenticationDialogProps {
  initShowDialog?: boolean
  explanation?: string | null
  initModalState?: ModalState
  bandId?: string | number | null
  defaultBandType?: string | null
  closeDialog?: (() => void) | null
}

export const AuthenticationDialog: React.FC<AuthenticationDialogProps> = ({
  initShowDialog = true,
  explanation = null,
  initModalState = "auth",
  bandId = null,
  defaultBandType = null,
  closeDialog = null,
}) => {
  const [showDialog, setShowDialog] = useState(initShowDialog)
  const [modalState, setModalState] = useState<ModalState>(initModalState)

  const { data: bandData } = useQuery(
    ["band", bandId],
    () =>
      request({
        queryKey: ["band", { path: `/api/v1/bands/${bandId}.json` }],
      }) as Promise<BandResponse>,
    { enabled: Boolean(bandId) },
  )

  const coverImageUrl = bandData?.band?.cover_photo_url_large ?? null
  const bandName = bandData?.band?.name ?? null

  const closeDialogFunction = closeDialog || (() => setShowDialog(false))

  const validationError = (data: AuthFlowData): string | null => {
    if (data.flow === "register" || data.flow === "authenticate") {
      const password = data.user.password
      if (password && (password.length < 6 || password.length > 24)) {
        return "Password must be between 6 and 24 characters long"
      }
    }
    return null
  }

  const dialogContentStyle: React.CSSProperties = coverImageUrl
    ? {
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.88)), url(${coverImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "40vh",
      }
    : undefined

  return (
    <>
      <Dialog
        isOpen={showDialog}
        onDismiss={closeDialogFunction}
        aria-label="login/signup dialog for users"
        className={bandId != null ? "AuthenticationDialog--bandContext" : undefined}
      >
        <div
          style={dialogContentStyle}
          className="AuthenticationDialog__content"
        >
          <div className="AuthenticationDialog__header">
            {bandName ? (
              <h2 className="AuthenticationDialog__title font-black text-2xl uppercase">
                {bandName}
              </h2>
            ) : (
              <span />
            )}
            <button
              className="AuthenticationDialog__close focus:outline-none"
              onClick={closeDialogFunction}
              type="button"
              aria-label="Close"
            >
              <span className="focus:outline-none text-lg" aria-hidden>
                ×
              </span>
            </button>
          </div>
          {explanation ? (
            <div className="font-bold mb-2 text-sm text-gray-700">
              {explanation}
            </div>
          ) : null}
          <div className="grid grid-cols-12 mt-3">
            {modalState === "register" ? (
              <RegistrationForm
                setModalState={setModalState}
                validationError={validationError}
                closeDialog={closeDialogFunction}
                defaultBandType={defaultBandType}
                followBandId={bandId}
              />
            ) : modalState === "auth" ? (
              <AuthenticationForm
                setModalState={setModalState}
                validationError={validationError}
                closeDialog={closeDialogFunction}
              />
            ) : modalState === "reset_password" ? (
              <ResetPasswordForm
                setModalState={setModalState}
                validationError={validationError}
                closeDialog={closeDialogFunction}
              />
            ) : null}
          </div>
        </div>
      </Dialog>
    </>
  )
}
